<i18n>
  {
    "fr": {
      "back-btn": "Retour au tableau de bord",
      "page-title": "Modifier mon profil",
      "change-user-email-success": "Un courriel de vérification a été envoyé à l’adresse {newEmail}",
      "error-email-already-assigned" : "Cette adresse courriel est déjà assignée à un compte"
    },
    "en": {
      "back-btn": "Back to dashboard",
      "page-title": "Edit my profile",
      "change-user-email-success": "A verification email has been sent to {newEmail}",
      "error-email-already-assigned" : "This email address is already assigned to an account"
    }
  }
  </i18n>

<template>
  <div class="card">
    <ui-box class="has-background-primary">
      <router-link :to="{ name: $consts.urls.URL_USER_DASHBOARD }" class="has-text-white">
        <span class="icon">
          <i class="fas fa-arrow-left"></i>
        </span>
        <span class="has-text-underline">{{ $t('back-btn') }}</span>
      </router-link>
      <h1 class="title is-2 has-text-weight-bold has-text-white mt-12px">{{ $t('page-title') }}</h1>
    </ui-box>
    <ui-box>
      <user-form
        v-if="user"
        is-edit
        is-self
        hide-roles
        show-change-email
        @submit="updateUser"
        @change-email="changeEmail"
        v-model="user"
        :loading="loading"
      />
      <s-loader :active="!user" />
    </ui-box>
  </div>
</template>

<script>
import Breadcrumbs from '@/mixins/breadcrumbs'
import UiBox from '@/components/ui/box'
import UserForm, { mapGraphToModel } from '@/components/businesses/user-form'

import AuthentificationService from '@/services/authentification'
import BusinessService from '@/services/business'
import { URL_USER_DASHBOARD } from '@/consts/urls'

export default {
  mixins: [Breadcrumbs],
  components: { UiBox, UserForm },
  data() {
    return {
      user: null,
      loading: false
    }
  },
  computed: {
    breadcrumbs() {
      return [{ label: this.$t('page-title') }]
    },
    currentUserId() {
      return this.me ? this.me.id : null
    },
    currentBusinessId() {
      return BusinessService.getCurrentBusiness().id
    }
  },
  methods: {
    async updateUser() {
      this.loading = true

      try {
        const input = {
          businessId: this.currentBusinessId,
          firstName: { value: this.user.firstName },
          lastName: { value: this.user.lastName },
          position: { value: this.user.position },
          language: { value: this.user.language }
        }

        await this.$apollo.mutate({
          mutation: this.$withFragments(this.$options.mutation.UpdateMyUserBusiness, UserForm.fragments.formFields),
          variables: {
            input
          }
        })

        this.$success()

        this.$router.push({ name: URL_USER_DASHBOARD })
      } finally {
        this.loading = false
      }
    },
    async changeEmail(newEmail, successCallback) {
      try {
        this.loading = true
        const input = { email: newEmail }

        await this.$apollo.mutate({
          mutation: this.$options.mutation.ChangeMyEmailRequest,
          variables: {
            input
          }
        })

        const msg = this.$t('change-user-email-success', { newEmail })
        this.$success(msg)

        if (successCallback) successCallback()
      } finally {
        this.loading = false
      }
    }
  },
  apollo: {
    me: {
      query() {
        return this.$options.query.Me
      },
      skip() {
        return !AuthentificationService.getUserIsLogged()
      }
    },
    userToEdit: {
      query() {
        return this.$withFragments(this.$options.query.UserBusiness, UserForm.fragments.formFields)
      },
      variables() {
        return {
          businessId: this.currentBusinessId,
          userId: this.currentUserId
        }
      },
      update(data) {
        if (!data) return
        this.user = mapGraphToModel(data.userBusiness)
      },
      skip() {
        return !this.currentUserId
      },
      fetchPolicy: 'network-only'
    }
  },
  gqlErrors() {
    return {
      EMAIL_ALREADY_ASSIGNED: this.$t('error-email-already-assigned')
    }
  }
}
</script>

<graphql>
query Me {
  me {
    id
  }
}

query UserBusiness($businessId: ID!, $userId: ID!) {
  userBusiness(businessId: $businessId, userId: $userId) {
    id
    ...formFields
  }
}

mutation UpdateMyUserBusiness($input: UpdateMyUserBusinessInput!) {
  updateMyUserBusiness(input: $input) {
    userBusiness {
      id
      ...formFields
    }
  }
}

mutation ChangeMyEmailRequest($input: ChangeUserEmailRequestInput!) {
  changeMyEmailRequest(input: $input)
}
</graphql>